import {ChangeEvent, forwardRef, ReactElement, useState} from 'react';
import {DefaultTheme} from 'styled-components';
import {
  ErrorText,
  IconWrapper,
  InputBlockWrapper,
  InputWrapper,
  Label,
} from './Input.styles';
import Text from '@atoms/Text';
import {theme} from '@styles/theme';
import {NumericFormat} from 'react-number-format';

export interface InputProps {
  id: string;
  name?: string;
  size?: number;
  placeholder?: string;
  value?: string | number;
  onChange?: (value: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (e: any) => void;
  label?: string;
  className?: string;
  style?: Record<string, any>;
  labelBGColor?: string;
  borderColor?: string;
  labelColor?: string;
  textColor?: string;
  error?: string | (() => string);
  hint?: string;
  withoutPadding?: boolean;
  placeholderColor?: string;
  placeholderFontSize?: number;
  iconSrc?: string;
  iconElement?: ReactElement;
  onClickIcon?: () => void;
  readonly?: boolean;
  disabled?: boolean;
  rounded?: boolean;
  withoutErrorText?: boolean;
  width?: string;
}

export interface StyledInputProps {
  labelBGColor?: string;
  labelColor?: string;
  borderColor?: string;
  textColor?: string;
  focus?: boolean;
  theme?: DefaultTheme;
  error?: string | undefined;
  withoutPadding?: boolean;
  placeholderColor?: string;
  placeholderFontSize?: number;
  rounded?: boolean;
  inputHaveValue?: boolean;
  placeholderLength?: number;
  disabled?: boolean;
  width?: string;
}

const NumberInput = forwardRef<HTMLInputElement, InputProps>(
  ({
    value,
    onChange,
    onFocus = () => false,
    onBlur,
    placeholder,
    id,
    name,
    label,
    style,
    labelBGColor,
    borderColor,
    labelColor,
    textColor,
    error,
    hint,
    withoutPadding = false,
    className,
    placeholderColor,
    placeholderFontSize,
    iconSrc,
    iconElement,
    onClickIcon,
    readonly,
    disabled = false,
    rounded,
    withoutErrorText = false,
    width,
  }) => {
    const [isFocus, setIsFocus] = useState(false);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      if (onChange) onChange(e);
    };

    return (
      <InputBlockWrapper width={width}>
        <InputWrapper
          onFocus={e => {
            onFocus(e);
            setIsFocus(!isFocus);
          }}
          focus={isFocus}
          error={typeof error === 'function' ? error() : error}
          style={style}
          borderColor={borderColor!}
          labelColor={labelColor!}
          textColor={textColor!}
          className={className}
          withoutPadding={withoutPadding}
          placeholderColor={placeholderColor!}
          placeholderFontSize={placeholderFontSize!}
          inputHaveValue={String(value).length !== 0}
          placeholderLength={placeholder?.length}
          rounded={rounded}
          title={label}
          disabled={disabled}>
          <Label
            htmlFor={id}
            labelBGColor={labelBGColor!}
            labelColor={labelColor!}
            error={typeof error === 'function' ? error() : error}
            inputHaveValue={value === 0 || !!value}
            focus={isFocus}>
            {label}
          </Label>
          <NumericFormat
            value={value}
            name={name}
            onChange={handleChange}
            decimalScale={2}
            fixedDecimalScale
            allowNegative={false}
            placeholder="00.00"
            className="your-input-class"
            onBlur={onBlur}
            readOnly={readonly}
            isAllowed={values => {
              const {floatValue, formattedValue} = values;
              // Проверяем числовое значение
              const isValidValue =
                floatValue === undefined || (floatValue >= 0 && floatValue <= 100);
              // Проверяем формат - не более 2 цифр до точки
              const isValidFormat =
                !formattedValue.includes('.') ||
                formattedValue.split('.')[0].length <= 2 ||
                formattedValue === '100.00';

              return isValidValue && isValidFormat;
            }}
          />
          {!withoutErrorText && error && (
            <ErrorText>{typeof error === 'function' ? error() : error}</ErrorText>
          )}
          {!error && iconSrc && (
            <IconWrapper onClick={onClickIcon}>
              <img src={iconSrc} width={24} height={24} alt="inputIcon" />
            </IconWrapper>
          )}
          {iconElement && <IconWrapper onClick={onClickIcon}>{iconElement}</IconWrapper>}
        </InputWrapper>
        {!!hint && (
          <Text text={hint} type="tinyR" color={theme.colors.basic03} align="right" />
        )}
      </InputBlockWrapper>
    );
  },
);

NumberInput.displayName = 'NumberInput';

export default NumberInput;
